import { TradeRouteSimple } from "@legacy-megarax/crm-contracts";
import { DateInput, TextInput } from "@legacy-megarax/ui-components";
import SaveIcon from "@mui/icons-material/Save";
import ScheduleIcon from "@mui/icons-material/Schedule";
import TransferWithinAStationIcon from "@mui/icons-material/TransferWithinAStation";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Radio,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Typography,
} from "@mui/material";
import React from "react";

import { useClientManager } from "@megaron/react-clients";

import { getSalespersonDisplayName } from "../../Home/TradeRoutesHome";
import { QueryFilters, SetQueryFilters, TravelData } from "../TradeRouteDetails";

interface Props {
  travelDataSum: TravelData | undefined;
  stopDurationsSum: number;
  onSchedule: (date: Date) => void;
  onSave: (name: string) => void;
  isLoading: boolean;
  userId: number | undefined;
  initialTradeRoute: TradeRouteSimple | null;
  isEdited: boolean;
  qs: QueryFilters;
  setQs: SetQueryFilters;
}

export const SaveButtons: React.FC<Props> = (props) => {
  return (
    <Box display="flex" flexDirection="column" mt="auto" height="80px">
      <NameSelectDialog
        action={(name) => props.onSave(name)}
        disabled={props.isLoading || props.userId === undefined || !props.isEdited}
        initialName={props.initialTradeRoute?.name ?? ""}
      >
        <Button color="primary" disabled={props.isLoading || !props.isEdited} sx={{ width: "100%" }}>
          Zapisz jako stała trasa
        </Button>
      </NameSelectDialog>
      <DateSelectDialog
        action={(date) => props.onSchedule(date)}
        disabled={props.isLoading || props.userId === undefined}
      >
        <Button color="primary" disabled={props.isLoading} sx={{ width: "100%" }}>
          Zaplanuj na dzień
        </Button>
      </DateSelectDialog>
    </Box>
  );
};

export const MobileSpeedDial: React.FC<Props> = ({ qs, setQs, ...props }) => {
  const enabled = props.userId !== undefined;

  const [open, setOpen] = React.useState(false);

  const profilesQuery = useClientManager("auth").userProfilesQuery().useQuery({});

  const profiles = profilesQuery.data?.items;

  return (
    <>
      <Dialog
        open={open || qs.userId === undefined}
        fullWidth
        maxWidth="xs"
        onClose={() => qs.userId && setOpen(false)}
      >
        <Box sx={{ overflowY: "auto" }}>
          <Box p={2} pb={1}>
            <Typography variant="subtitle2">Wybierz Handlowca:</Typography>
          </Box>
          <List>
            {profiles &&
              profiles.map((salesperson, idx) => {
                const selected = qs.userId === salesperson.legacyUserId;
                return (
                  <ListItem
                    button
                    key={salesperson.legacyUserId}
                    sx={{ backgroundColor: selected ? "#d1e6d6" : "transparent" }}
                    secondaryAction={<Radio checked={selected} sx={{ pointerEvents: "none" }} />}
                    onClick={() => {
                      setQs("userId", salesperson.legacyUserId);
                      setOpen(false);
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        alt={getSalespersonDisplayName(salesperson) ?? idx.toString()}
                        src={salesperson.pictureUrl ?? ""}
                      />
                    </ListItemAvatar>
                    <ListItemText>{getSalespersonDisplayName(salesperson) ?? "Nieznany Handlowiec"}</ListItemText>
                  </ListItem>
                );
              })}
          </List>
        </Box>
      </Dialog>
      <SpeedDial
        ariaLabel={"Mobile menu"}
        sx={{ position: "absolute", bottom: 70, left: 15, zIndex: 1000 }}
        icon={<SpeedDialIcon />}
      >
        <SpeedDialAction
          key="salesperson"
          icon={<TransferWithinAStationIcon />}
          tooltipOpen
          tooltipTitle="Handlowiec"
          tooltipPlacement="right"
          onClick={() => setOpen(true)}
        />
        <SpeedDialAction
          key="save"
          icon={
            <NameSelectDialog
              action={(name) => props.onSave(name)}
              disabled={props.isLoading || !enabled || !props.isEdited}
              initialName={props.initialTradeRoute?.name ?? ""}
            >
              <IconButton disabled={props.isLoading || !enabled || !props.isEdited}>
                <SaveIcon />
              </IconButton>
            </NameSelectDialog>
          }
          tooltipOpen
          tooltipTitle="Zapisz"
          tooltipPlacement="right"
        />
        <SpeedDialAction
          key="schedule"
          icon={
            <DateSelectDialog action={(date) => props.onSchedule(date)} disabled={props.isLoading || !enabled}>
              <ScheduleIcon />
            </DateSelectDialog>
          }
          tooltipOpen
          tooltipTitle="Zaplanuj"
          tooltipPlacement="right"
        />
      </SpeedDial>
    </>
  );
};

const NameSelectDialog: React.FC<{
  action: (name: string) => void;
  disabled: boolean;
  initialName: string;
  children: React.ReactNode;
}> = (props) => {
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [name, setName] = React.useState<string>(props.initialName);

  const onAccept = () => {
    if (!name || props.disabled) return;
    props.action(name);
  };

  React.useEffect(() => {
    setName(props.initialName);
  }, [props.initialName]);

  return (
    <>
      <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
        <DialogTitle>Nazwij trasę</DialogTitle>
        <DialogContent>
          <TextInput value={name} onChange={setName} label="Nazwa" />
        </DialogContent>
        <DialogActions>
          <Box mr={"auto"}>
            <Button onClick={() => setIsOpen(false)}>Anuluj</Button>
          </Box>
          <Button onClick={onAccept} color={"primary"} disabled={props.disabled}>
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>
      <Box display="flex" justifyContent="center" onClick={() => !props.disabled && setIsOpen(true)}>
        {props.children}
      </Box>
    </>
  );
};

const DateSelectDialog: React.FC<{
  action: (date: Date) => void;
  disabled: boolean;
  children: React.ReactNode;
}> = (props) => {
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [date, setDate] = React.useState<Date | null>(null);

  const onAccept = () => {
    if (!date || props.disabled) return;
    props.action(date);
  };

  React.useEffect(() => {
    if (isOpen) setDate(null);
  }, [isOpen]);

  return (
    <>
      <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
        <DialogTitle>Wybierz datę</DialogTitle>
        <DialogContent>
          <DateInput
            disablePast={undefined}
            inputFormat={undefined}
            maxDate={undefined}
            minDate={undefined}
            value={date}
            onChange={setDate}
            label="Data"
          />
        </DialogContent>
        <DialogActions>
          <Box mr={"auto"}>
            <Button onClick={() => setIsOpen(false)}>Anuluj</Button>
          </Box>
          <Button onClick={onAccept} color={"primary"} disabled={props.disabled}>
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>
      <Box display="flex" justifyContent="center" onClick={() => !props.disabled && setIsOpen(true)}>
        {props.children}
      </Box>
    </>
  );
};
